import { Button, FormControl, Grid, InputLabel, MenuItem, Paper, Select, Tooltip, Typography, styled } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { InputSelect } from '../../_components/DesignLab';
import { useTranslation } from 'react-i18next';
import useLabData from '../../_hooks/useLabData';
import { AddImgLayer, EditActiveVariant, EditLayer } from '../../../UIData/_actions/DesignLabStoreActions';
import { UserImageStore } from '../../../UserData/_stores/UserImageStore';
import { LayerPatternMode } from '../../../UIData/_stores/DesignLab/DesignLabLayerStore';
import ProductOptionStore from '../../../AppData/_stores/ProductOptionStore';
import { useAppDispatch } from '../../../Common/_hooks/useAppDispatch';
import ImageLibraryBrowser from '../../ImageLibraryBrowser/_components/ImageLibraryBrowser';
import { useAppSelector } from '../../../Common/_hooks/useAppSelector';
import { LabWrapper, LeftContainer } from '../../_components/LabInterface';
import SizeDisplay from '../../../Common/Measurements/SizeDisplay';
import { getValidImageDpi } from '../../../Common/Measurements/imageResolution';
import FabricScaleSlider from './FabricScaleSlider';
import useCalcFitTemplate from '../../_hooks/useCalcFitTemplate';
import { clampInitialImageScale, MIN_FABRIC_IMAGE_SCALE } from '../../imageSizing';

const ArrowWhite = styled('img')({
    marginLeft: '15px',

    '@media (max-width: 1200px), (max-height: 900px)': {
        width: '30px',
    },
})

const FabricsList = styled('div')(({theme}) => ({
    width: '300px',
    padding: '0 15px',

    '&.hidden': {
        transform: 'translateX(-100px)',
        opacity: 0,
    },

    ['@media (max-width: '+theme.breakpoints.values.lab_mobile+'px), (max-height: '+theme.height_breakpoints.values.lab_mobile+'px)']: {
        transform: 'translateX(-100px)',
        opacity: 0,

        '&.active': {
            opacity: 1,
            transform: 'none',
        },
    }
}));

const FabricItem = styled('div')(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    marginBottom: '15px',
    paddingRight: '15px',
    cursor: 'pointer',
    transition: 'background-color 250ms',
    borderRadius: '20px',
    '&.selected, &:hover': {
        backgroundColor: '#E8E8E8',
    }
}));

const WhiteGrid = styled(Grid)(({theme}) => ({
    padding: '40px',

    ['@media (max-width: '+theme.breakpoints.values.lab_mobile+'px), (max-height: '+theme.height_breakpoints.values.lab_mobile+'px)']: {
        padding: '15px',
    },
}));

const FabricImageDrop = styled('div')(({theme}) => ({
    background: '#EBEBEB',
    border: 'dashed 2px #707070',
    borderRadius: '20px',
    height: '100%',
    minHeight: 350,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '15px',

    ['@media (max-width: '+theme.breakpoints.values.sm+'px)']: {
        minHeight: 0,
        height: 150
    }
}));

const BlueGrid = styled(Grid)(({theme}) => ({
    padding: '60px',
    background: '#F2F6F8',
}));

const BlueGridTitle = styled('div')(({theme}) => ({
    textAlign: 'center', 
    marginBottom: '30px',
    fontSize: '24px',
}));

const SelectedFabricSpecs = styled('div')(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    marginBottom: '5px',
    'img': {
        width: '25px',
        marginRight: '10px',
    }
}));

const RepeatOptions = styled('div')(({theme}) => ({
    display: 'flex',
    justifyContent: 'flex-start',
    gap: 10,
    margin: '15px 0 30px',
    flexWrap: 'wrap',
}));

const RepeatOptionItem = styled('div')(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50px',
    background: '#EBEBEB',
    height: '50px',
    width: '50px',
    padding: '8px',
    cursor: 'pointer',
    'img': {
        maxWidth: '100%',
    }
}));

const CustomInputLabel = styled(InputLabel)(({theme}) => ({
    marginTop: '30px',
    marginBottom: '10px',
    color: '#000',
}));


const LayoutFormControl = styled(FormControl)`
    height: 45px;
    border-radius: 30px;
    background: #EBEBEB;
    color: #000;
    padding: 0 15px;
    width: 100%;
`

const FabricSeePreviewButton = styled(Button)(({theme}) => ({
    position: 'absolute',
    zIndex: 30,
    padding: '6px 20px',
    display: 'none',
    background: 'linear-gradient(0deg, #DA3192 0%, #F600BE 100%)',
    color: '#fff',
    borderRadius: '30px',
    fontWeight: 400,
    bottom: '15px',
    top: 'auto',
    margin: 'auto',
    left: 0,
    right: 0,
    width: 'fit-content',

    ['@media (max-width: '+theme.breakpoints.values.lab_mobile+'px), (max-height: '+theme.height_breakpoints.values.lab_mobile+'px)']: {
        display: 'flex',
        alignItems: 'center',
    },
}));

const StageContainer = styled('div')(({theme}) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 300ms, opacity 300ms',
    position: 'relative',
    opacity: 1,
    paddingLeft: 30,

    '&.hidden': {
        transform: 'translateY(100px)',
        opacity: 0,
    },

    '@media (max-width: 1510px), (max-height: 1070px)': {
        paddingLeft: '15px',
    },

    ['@media (max-width: '+theme.breakpoints.values.lab_mobile+'px), (max-height: '+theme.height_breakpoints.values.lab_mobile+'px)']: {
        paddingRight: '15px',
        transform: 'translateY(100px)',
        opacity: 0,

        '&.active': {
            opacity: 1,
            transform: 'none',
        }
    },
}));

type FabricRepeatMode = 'none'|'repeat'|'repeat-mirror'|'half-drop'|'half-brick'|'repeat-mirror-x'|'repeat-mirror-y';

const FABRIC_REPEAT_MODES:FabricRepeatMode[] = [
    'none',
    'repeat',
    'repeat-mirror',
    'repeat-mirror-x',
    'repeat-mirror-y',
    'half-drop',
    'half-brick',
];

type Props = {
    hide?: boolean
    active?: boolean
    onNext: () => void
}

interface PatternSize {
    sizes: number[]
    dpi: number
    printDpi: number
}


export default function FabricLab(props: Props) {
    const dispatch = useAppDispatch();
    const [t] = useTranslation('design-lab');
    const activeScene = useAppSelector(state => state.get('UIData').get('designLab').get('activeScene'));
    const activeSubproduct = useAppSelector(state => state.get('UIData').get('designLab').get('activeSubproduct'));
    const activeVariant = useAppSelector(state => state.get('UIData').get('designLab').get('activeVariant'));
    const activeOptions = useAppSelector(state => state.get('UIData').get('designLab').get('activeOptions'));
    const firstLayer = useAppSelector(state => state.get('UIData').get('designLab').get('layers').first());
    const labData = useLabData();
    const calcFitTemplate = useCalcFitTemplate(labData?.variants[activeVariant]?.scenes.main);
    const productData = useAppSelector(state => state.get('appData').get('products').get('50'));
    const [searchParams] = useSearchParams();

    const [libraryOpened,setLibraryOpened] = useState(false)

    const fabricOptionType = useMemo(() => {
        return productData?.get('availableOptionTypes').get('2');
    }, [productData]);

    const fabricSizeOptionType = useMemo(() => {
        return productData?.get('availableOptionTypes').get('13');
    }, [productData]);

    const listFabrics = useMemo(() => {
        return fabricOptionType?.get('options');
    }, [fabricOptionType]);

    const getFabricSizes = useCallback((fabric: ProductOptionStore | undefined) => {
        if(!fabric) return [];

        const tmpList:ProductOptionStore[] = [];

        fabricSizeOptionType?.get('options').forEach(option => {
            //Check for fabric dependency
            if(!option.get('listDependencies').includes(fabric.get('id'))) {
                return;
            }

            tmpList.push(option);
        });
        
        return tmpList
    }, [fabricSizeOptionType]);

    const routeFabricSlug = useMemo(() => {
        return fabricOptionType ? searchParams.get('option_' + fabricOptionType.get('slug')) : null;
    }, [fabricOptionType, searchParams]);

    const routeFabricSizeSlug = useMemo(() => {
        return fabricSizeOptionType ? searchParams.get('option_' + fabricSizeOptionType.get('slug')) : null;
    }, [fabricSizeOptionType, searchParams]);

    const activeVariantParts = useMemo(() => {
        return activeVariant.split('|');
    }, [activeVariant]);

    const selectedFabric = useMemo(() => {
        if(!listFabrics) return undefined;

        return listFabrics.find(fabric => fabric.get('slug') === activeVariantParts[0]);
    }, [activeVariantParts, listFabrics]);

    const listFabricSizes = useMemo(() => {
        return getFabricSizes(selectedFabric);
    }, [getFabricSizes, selectedFabric]);

    useEffect(() => {
        if(!listFabrics) return;

        const fabric = selectedFabric
            ?? (routeFabricSlug ? listFabrics.find(fabric => fabric.get('slug') === routeFabricSlug) : undefined)
            ?? listFabrics.first();

        if(!fabric) return;

        const listSizes = getFabricSizes(fabric);
        const defaultSize = listSizes[0] as ProductOptionStore | undefined;
        const size = listSizes.find(size => size.get('slug') === activeVariantParts[1])
            ?? (routeFabricSizeSlug ? listSizes.find(size => size.get('slug') === routeFabricSizeSlug) : undefined)
            ?? listSizes.find(size => size.get('slug') === '1m')
            ?? defaultSize;

        if(size === undefined) return;

        const nextVariant = fabric.get('slug')+'|'+size.get('slug');

        if(activeVariant !== nextVariant) {
            dispatch(EditActiveVariant(nextVariant));
        }
    }, [activeVariant, activeVariantParts, dispatch, getFabricSizes, listFabrics, routeFabricSizeSlug, routeFabricSlug, selectedFabric]);

    const onFabricChange = useCallback((fabricId: number) => {
        const fabric = listFabrics?.get(String(fabricId));
        if(!fabric) return;

        const listSizes = getFabricSizes(fabric);
        const defaultSize = listSizes[0] as ProductOptionStore | undefined;
        const size = listSizes.find(size => size.get('slug') === activeVariantParts[1])
            ?? listSizes.find(size => size.get('slug') === '1m')
            ?? defaultSize;

        if(size === undefined) return;

        dispatch(EditActiveVariant(fabric.get('slug')+'|'+size.get('slug')));
    }, [activeVariantParts, dispatch, getFabricSizes, listFabrics]);

    const repeatMode = useMemo(() => {
        if(!firstLayer) return 'none';
        const patternMode = firstLayer.get('patternMode');

        if(['repeat','repeat-mirror','repeat-mirror-x','repeat-mirror-y','half-drop','half-brick'].includes(patternMode)) {
            return patternMode;
        }

        return 'none';
    }, [firstLayer]);

    const defaultPatternMode = useMemo((): LayerPatternMode => {
        return activeOptions.get('layout') === 'centered' ? 'none' : 'repeat';
    }, [activeOptions]);

    const onSelectImage = useCallback((item:UserImageStore) => {
        const originalWidth = item.get('width');
        const originalHeight = item.get('height');
        const defaultDpi = getValidImageDpi(labData?.dpi) ?? 150;
        const width = originalWidth*30/defaultDpi;
        const height = originalHeight*30/defaultDpi;
        const imageDpi = getValidImageDpi(item.get('dpi'));
        const scale = imageDpi === undefined ? 1 : clampInitialImageScale(
            defaultDpi / imageDpi, MIN_FABRIC_IMAGE_SCALE, calcFitTemplate(width, height).scaleX,
        );
        const patternMode = firstLayer?.get('patternMode') ?? defaultPatternMode;
        const alignment = firstLayer?.get('alignment') ?? (patternMode === 'none' ? 'center' : 'top-left');

        if(firstLayer) {
            dispatch(EditLayer(firstLayer.get('id'), {
                fileid: item.get('fileid'),
                src: '/api/user/images/image/'+item.get('fileid'),
                originalWidth: originalWidth,
                originalHeight: originalHeight,
                width: width,
                height: height,
                x: 0,
                y: 0,
                scaleX: scale,
                scaleY: scale,
                rotation: 0,
                alignment: alignment,
                patternMode: patternMode,
            }))
        } else{
            dispatch(AddImgLayer({
                fileid: item.get('fileid'),
                src: '/api/user/images/image/'+item.get('fileid'),
                originalWidth: originalWidth,
                originalHeight: originalHeight,
                subproduct: null,
                scene: 'main',
                width: width,
                height: height,
                x: 0,
                y: 0,
                scaleX: scale,
                scaleY: scale,
                rotation: 0,
                alignment: alignment,
                patternMode: patternMode,
            }))
        }
    }, [labData, activeScene, activeSubproduct, activeVariant, firstLayer, defaultPatternMode, calcFitTemplate]);

    const repeatModeNames = useMemo(() => {
        return {
            'none': t('No repeat'),
            'repeat': t('Block'),
            'repeat-mirror': t('Mirror'),
            'repeat-mirror-x': t('Mirror Horizontal'),
            'repeat-mirror-y': t('Mirror Vertical'),
            'half-drop': t('Half-Drop'),
            'half-brick': t('Half-Brick'),
        }
    }, [t]);

    const editRepeat = useCallback((repeat:LayerPatternMode) => {
        if(!firstLayer) return;

        dispatch(EditLayer(firstLayer.get('id'), {
            patternMode: repeat,
            alignment: repeat === 'none' ? 'center' : 'top-left',
        }))
    }, [firstLayer, activeVariant]);

    const onRepeatClick:Record<LayerPatternMode, () => void> = useMemo(() => {
        let tmpList:Record<string, () => void> = {};
        FABRIC_REPEAT_MODES.forEach(mode => {
            tmpList[mode] = () => editRepeat(mode);
        });
        return tmpList;
    }, [editRepeat]);

    const renderRepeatMode = useCallback((mode:FabricRepeatMode) => {
        const active = mode === repeatMode ? 'active':'';
        return <Tooltip
            key={mode}
            title={repeatModeNames[mode]}
            placement="bottom"
            disableInteractive
        >
            <RepeatOptionItem 
                onClick={onRepeatClick[mode]}
                className={mode === repeatMode ? 'active':''}
            >
                <img src={require('@resources/img/create/design-lab-v4/icons/repeat/'+mode+(active ? '-'+active:'')+'.svg?url')}/>
            </RepeatOptionItem>
        </Tooltip>
    }, [repeatMode, onRepeatClick, repeatModeNames]);

    const patternSize = useMemo((): PatternSize | undefined => {
        if(!labData || !firstLayer) return undefined;

        return {
            sizes: [
                firstLayer.get('originalWidth')*firstLayer.get('scaleX'),
                firstLayer.get('originalHeight')*firstLayer.get('scaleY'),
            ],
            dpi: labData.dpi,
            printDpi: Math.round(labData.dpi*(1 / firstLayer.get('scaleX'))*10)/10,
        }
    }, [firstLayer, labData?.dpi]);

    const onLibraryOpen = useCallback(() => {
        setLibraryOpened(true)
    }, [])

    const onLibraryClose = useCallback(() => {
        setLibraryOpened(false)
    }, [])

    return <LeftContainer className={props.active ? 'active':''}>
        { /* <FabricsList className={props.hide ? 'hidden':(props.active ? 'active':'')}>
                <ListTitle style={{padding: '0',}}>Fabrics</ListTitle>
                <div>
                    { listFabrics ? listFabrics.valueSeq().map(fabric => {
                        return <FabricItem
                            key={fabric.get('id')}
                        >
                            <img 
                                src={'/static/img/all-products/products/'+fabric.get('slug')+'-fabric.jpg'}
                                style={{
                                    maxWidth: '85px', 
                                    maxHeight: '85px', 
                                    borderRadius: '15px',
                                    marginRight: '10px',
                                }} 
                            />
                            <Typography variant="body1" style={{
                                flex: '1',
                            }}>{ fabric.get('name') }</Typography>

                            <ValidIndicator 
                                valid={true} 
                                iconSize="small"
                                className="selected" 
                                style={{
                                    width: '25px',
                                }} 
                            />
                        </FabricItem>
                    }) : null }
                </div>
        </FabricsList> */ }
        <StageContainer 
            className={props.hide ? 'hidden':(props.active ? 'active':'')}
        >
            <FabricSeePreviewButton
                onClick={props.onNext}
            >{ t('Next') }<ArrowWhite src={require('@resources/img/create/design-lab-v4/icons/white-arrow.svg?url')}/></FabricSeePreviewButton>
            <LabWrapper
                style={{

                }}
            >
                <Paper 
                    elevation={3}
                    style={{
                        display: 'flex',
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexFlow: 'column',
                        borderRadius: '20px 20px 0 0',
                        overflow: 'hidden',
                    }}
                >
                    <WhiteGrid container spacing={2}>
                        <Grid item xs={12} sm={5} xl={6}>
                                <FabricImageDrop style={{
                                    position: 'relative',
                                    overflow: 'hidden',
                                }}>
                                    { firstLayer ? <img 
                                        src={firstLayer.get('src')}
                                        style={{
                                            position: 'absolute',
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        }}
                                    /> : null }
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="large"
                                        onClick={onLibraryOpen}
                                        startIcon={<img src={require('@resources/img/create/design-lab-v4/icons/add-image.svg?url')} alt={t('Add Image icon')} />}
                                    >{t('Upload Image')}</Button>
                                </FabricImageDrop>
                        </Grid>
                        <Grid item xs={12} sm={7} xl={6}>
                            { firstLayer ? <>
                                <CustomInputLabel id="layout" style={{marginTop: '0'}}>{ t('Repeat') }</CustomInputLabel>
                                <RepeatOptions>
                                    { FABRIC_REPEAT_MODES.map(renderRepeatMode) }
                                </RepeatOptions>

                                <CustomInputLabel id="fabric">{ t('Fabric') }</CustomInputLabel>
                                { listFabrics ? <LayoutFormControl variant="standard">
                                    <Select
                                        labelId="fabric"
                                        style={{color: '#000'}}
                                        variant="standard"
                                        input={<InputSelect/>}
                                        value={selectedFabric?.get('id') ?? ''}
                                        onChange={(e) => {
                                            onFabricChange(Number(e.target.value));
                                        }}
                                        MenuProps={{
                                            anchorOrigin: {
                                                vertical: 'bottom',
                                                horizontal: 'center',
                                            },
                                            transformOrigin: {
                                                vertical: 'top',
                                                horizontal: 'center',
                                            }
                                        }}
                                        sx={{
                                            '.MuiSelect-icon': {
                                                color: '#000',
                                            }
                                        }}
                                    >
                                        { listFabrics.valueSeq().map(fabric => {
                                            return <MenuItem key={fabric.get('id')} value={fabric.get('id')}>{fabric.get('name')}</MenuItem>
                                        }) }
                                    </Select>
                                </LayoutFormControl> : null }

                                <CustomInputLabel id="fabric-size">{ t('Fabric Size') }</CustomInputLabel>
                                <LayoutFormControl variant="standard">
                                    <Select
                                        labelId="fabric-size"
                                        style={{color: '#000'}}
                                        variant="standard"
                                        input={<InputSelect/>}
                                        value={activeVariant}
                                        onChange={(e) => {
                                            dispatch(EditActiveVariant(e.target.value));
                                        }}
                                        MenuProps={{
                                            anchorOrigin: {
                                                vertical: 'bottom',
                                                horizontal: 'center',
                                            },
                                            transformOrigin: {
                                                vertical: 'top',
                                                horizontal: 'center',
                                            }
                                        }}
                                        sx={{
                                            '.MuiSelect-icon': {
                                                color: '#000',
                                            }
                                        }}
                                    >
                                        { listFabricSizes.map(size => {
                                            const fabric = selectedFabric;
                                            if(!fabric) return null;

                                            return <MenuItem key={size.get('id')} value={fabric.get('slug')+'|'+size.get('slug')}>{size.get('name')}</MenuItem>
                                        }) }
                                    </Select>
                                </LayoutFormControl>

                                <CustomInputLabel id="pattern-size">
                                    { t('Pattern Size') }
                                    {patternSize !== undefined ? <> (<SizeDisplay sizes={patternSize.sizes} sourceUnit="px" dpi={patternSize.dpi} displayType="physical" />, {patternSize.printDpi} DPI)</> : null}
                                </CustomInputLabel>
                                <FabricScaleSlider
                                    key={firstLayer.get('fileid')}
                                    label={t('Pattern Size')}
                                    value={firstLayer.get('scaleX')}
                                    onChange={newScale => {
                                        dispatch(EditLayer(firstLayer.get('id'), {
                                            scaleX: newScale,
                                            scaleY: newScale,
                                        }))
                                    }}
                                />
                            </> : null }
                        </Grid>
                    </WhiteGrid>
                    { /*<BlueGrid container spacing={2}>
                        <Grid item xs={12}>
                        <BlueGridTitle>You have selected : <strong>Silk Habotai</strong></BlueGridTitle>
                        </Grid>
                        <Grid item xs={6}>
                            <SelectedFabricSpecs>
                                <img src="static/img/pages/fabric/design-2020/lightweight.svg" alt="Weight"/>
                                Weight: 275g/m2
                            </SelectedFabricSpecs>
                            <SelectedFabricSpecs>
                                <img src="static/img/pages/fabric/design-2020/sizes.svg" alt="Printable Width"/>
                                Printable Width: 55”/ 140cm
                            </SelectedFabricSpecs>
                            <SelectedFabricSpecs>
                                <img src="static/img/pages/fabric/design-2020/knit.svg" alt="Content"/>
                                Content: 100% cotton
                            </SelectedFabricSpecs>
                            <SelectedFabricSpecs>
                                <img src="static/img/pages/fabric/design-2020/color.svg" alt="Color"/>
                                Color: Off-white
                            </SelectedFabricSpecs>
                            <SelectedFabricSpecs>
                                <img src="static/img/pages/fabric/design-2020/print.svg" alt="Print Through"/>
                                Print Through: Low- roughly 10%
                            </SelectedFabricSpecs>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="h4" style={{marginBottom: '0'}}>Features</Typography>
                            <Typography variant="body1" style={{marginBottom: '30px'}}>
                                <ul style={{marginTop: '0'}}>
                                    <li>Classic weave quilting cotton made from quality long staple cotton fibers.</li>
                                    <li>Easy to work with.</li>
                                    <li>Smooth, untextured print surface.</li>
                                </ul>
                            </Typography>
                            <Typography variant="h4" style={{marginBottom: '0'}}>Uses</Typography>
                            <Typography variant="body1" style={{marginBottom: '30px'}}>
                                <ul style={{marginTop: '0'}}>
                                    <li>Quilting & Crafts</li>
                                    <li>Lightweight clothing</li>
                                    <li>Accessories</li>
                                </ul>
                            </Typography>
                        </Grid>
                    </BlueGrid> */ }
                </Paper>
            </LabWrapper>
        </StageContainer>

        <ImageLibraryBrowser
            opened={libraryOpened}
            onSelect={onSelectImage}
            onClose={onLibraryClose}
        />
    </LeftContainer>
}
